import PropTypes from 'prop-types';
import React, {Component} from 'react';
import CSSTransition from 'react-transition-group/CSSTransition';
import {LoadingOverlay} from '@homeaway/react-loading-overlay';
import {SVGIcon} from '@homeaway/react-svg';
import {CLOSE__16} from '@homeaway/svg-defs/lib/defs/CLOSE__16';
import classnames from 'classnames';
/* eslint-disable no-multi-spaces */

class ModalStructure extends Component {
    static propTypes = {
        /** id attribute for the modal. Used to generate aria-labeledby attributes as well */
        id: PropTypes.string.isRequired,
        /** Flag to determine if the Modal has a close "x" button in the header */
        closeButton: PropTypes.bool,
        /** Screen Reader only text for close button in Modal Header */
        closeText: PropTypes.string.isRequired,
        /** Flag for if the Modal Header is shown */
        header: PropTypes.bool,
        /** The Modal Heading in .modal-title. Used as the label for the modal if provided. */
        title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
        /** React Component for Modal footer */
        footer: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
        /** Flag to determine if the Modal should display a loading overlay */
        isLoading: PropTypes.bool,
        /** Flag to determine if the Modal should be styled as "instructional" */
        isInstructional: PropTypes.bool,
        /** Flag to determine if the Modal should be styled as "modal-content--workflow" (more compact mobile styling) */
        isWorkflow: PropTypes.bool,
        /** ReactCSSTransitionGroup transitionName for modal animation */
        modalAnimation: PropTypes.oneOf(['modal-from-center', 'modal-from-bottom', 'modal-from-left', 'modal-from-right', 'none']),
        /** Flag to determine if the dark backdrop should be rendered. **/
        renderBackdrop: PropTypes.bool,
        /** Timeout object for transitions */
        timeouts: PropTypes.shape({
            backdrop: PropTypes.shape({
                transitionEnterTimeout: PropTypes.number,
                transitionExitTimeout: PropTypes.number
            }),
            modal: PropTypes.shape({
                transitionEnterTimeout: PropTypes.number,
                transitionExitTimeout: PropTypes.number
            })
        })
    }

    static defaultProps = {
        closeButton: null,
        header: null,
        footer: null,
        modalAnimation: 'modal-from-center',
        renderBackdrop: true,
        title: null,
        timeouts: {
            backdrop: {
                transitionEnterTimeout: 250,
                transitionExitTimeout: 250
            },
            modal: {
                transitionEnterTimeout: 350,
                transitionExitTimeout: 350
            }
        }
    }

    constructor(props) {
        super(props);

        this.handleModalClick = this.handleModalClick.bind(this);
        this.renderBackdrop = this.renderBackdrop.bind(this);
    }

    /** The .modal-overlay is class is contained WITHIN the Modal markup itself,
     *  thus we need to manually handle determining if the user is clicking outside
     *  the modal dialog.
     */
    handleModalClick(e) {
        // Do not do anything is we are specifying false for closeOnOutsideClick
        if (this.props.closeOnOutsideClick) {
            // currentTarget here is the `modal` div, which covers the entire screen
            // any clicks on the backdrop will have a target of modal
            // any clicks on the modal itself will have a target of modal-body, model-header, etc
            if (e.target !== e.currentTarget) {
                return;
            }
            e.stopPropagation();
            this.props.onClose();
        }
    }

    // Helps with iOS specific bug: swiping on header or footer steals some sort of
    // focus that should be on modal-body, thus causing natural scrolling to freeze
    // until modal-body is tapped again
    preventTouchMove(e) {
        e.preventDefault();
    }

    renderBackdrop() {
        return (
            <div tabIndex="-1" className="Modal__backdrop" />
        );
    }

    renderModalContent() {
        const {
            children,
            className,
            closeButton,
            closeText,
            header,
            footer,
            id,
            isInstructional,
            isLoading,
            isWorkflow,
            onClose,
            modalBodyRef,
            title,
            // grab unused vars to prevent rest from being passed down as propertys on the modal div
            closeOnEsc,             // eslint-disable-line no-unused-vars
            closeOnOutsideClick,    // eslint-disable-line no-unused-vars
            isOpen,                 // eslint-disable-line no-unused-vars
            modalAnimation,         // eslint-disable-line no-unused-vars
            timeouts                // eslint-disable-line no-unused-vars
        } = this.props;

        const modalStyles = {
            display: 'block'
        };

        const modalClassName = classnames(className, 'Modal');
        const modalContentClassName = classnames(
            'Modal__content',
            isInstructional ? 'Modal__content--instructional' : '',
            isWorkflow ? 'Modal__content--workflow' : ''
        );

        return (
            <div
                ref={(c) => {
                    this.dialog = c;
                }}
                className={modalClassName}
                id={id}
                style={modalStyles}
                onClick={this.handleModalClick}
                tabIndex="-1"
                role="dialog"
                aria-labelledby={`${id}-label`}
                aria-modal="true"
            >
                <div className="Modal__dialog">
                    <div className={modalContentClassName} role="document">
                        {isLoading &&
                            <LoadingOverlay/>
                        }
                        {header &&
                            <div onTouchMove={this.preventTouchMove} className="Modal__header">
                                {closeButton &&
                                    <button type="button" onClick={onClose} className="Modal__close-btn" aria-label={closeText}>
                                        <span className="Modal__close-flex" aria-hidden="true">
                                            <SVGIcon markup={CLOSE__16} />
                                        </span>
                                    </button>
                                }
                                {title &&
                                    <h4 id={`${id}-label`} className="Modal__title">{title}</h4>
                                }
                            </div>
                        }
                        <div ref={modalBodyRef} className="Modal__body">
                            {children}
                        </div>
                        {footer &&
                            <div onTouchMove={this.preventTouchMove} className="Modal__footer">
                                {footer}
                            </div>
                        }
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const {
            // grab unused vars to prevent rest from being passed down as propertys on the modal div
            closeOnEsc,             // eslint-disable-line no-unused-vars
            closeOnOutsideClick,    // eslint-disable-line no-unused-vars
            isOpen,
            modalAnimation,
            renderBackdrop,
            timeouts
        } = this.props;

        const useModalAnimation = modalAnimation !== 'none';

        return (
            <div>
                {renderBackdrop && useModalAnimation &&
                    <CSSTransition
                        in={isOpen}
                        classNames="Modal__backdrop"
                        timeout={{
                            enter: timeouts.backdrop.transitionEnterTimeout,
                            exit: timeouts.backdrop.transitionExitTimeout
                        }}
                        mountOnEnter
                        unmountOnExit
                    >
                        {this.renderBackdrop()}
                    </CSSTransition>
                }
                {renderBackdrop && !useModalAnimation && isOpen && this.renderBackdrop()}
                {useModalAnimation &&
                    <CSSTransition
                        in={isOpen}
                        classNames={modalAnimation}
                        timeout={{
                            enter: timeouts.modal.transitionEnterTimeout,
                            exit: timeouts.modal.transitionExitTimeout
                        }}
                        mountOnEnter
                        unmountOnExit
                    >
                        {this.renderModalContent()}
                    </CSSTransition>
                }
                {!useModalAnimation && isOpen && this.renderModalContent()}
            </div>
        );
    }
}

export default ModalStructure;
